import React, { useCallback, useMemo } from 'react';

import classnames from 'classnames';

import { useHorizontalScroller } from 'components/uiLibrary/HorizontalScroller';
import { QuaternaryButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';

import { SECTIONS } from 'components/Globals/Analytics/constants';

import { TP } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';

import { useTranslation } from 'src/i18n';

import { QUERY_PARAMS_RESERVED, VIEW_MODES } from 'operabase-router/constants';

import classes from './ProductionViewSwitcher.module.scss';

const ProductionViewSwitcher = ({ value: selectedViewMode, setViewMode }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isDesktop, isMobile } = useDeviceTypeLayouts();
  const navigate = useNavigate();
  const { obRouteContext } = useAppContext();
  const { resetScrollPosition } = useHorizontalScroller();

  const getLinkProps = useCallback(
    viewMode =>
      navigate.getLinkProps({
        ...obRouteContext?.linkProps,
        queryParams: {
          ...obRouteContext?.queryParams,
          [QUERY_PARAMS_RESERVED.VIEW_MODE]: viewMode,
        },
      }),
    [navigate, obRouteContext?.linkProps, obRouteContext?.queryParams],
  );

  const options = useMemo(() => {
    const validOptions = [
      {
        label: t(`${TP}.FN_DAY`),
        value: VIEW_MODES.DAY,
        icon: 'calendar_clock',
      },
      {
        label: t(`${TP}.FN_SEASON`),
        value: VIEW_MODES.SEASON,
        icon: 'ballot',
      },
      ...(isDesktop
        ? [
            {
              label: t(`${TP}.FN_CALENDAR`),
              value: VIEW_MODES.CALENDAR,
              icon: 'calendar',
            },
          ]
        : [
            {
              label: t(`${TP}.FN_VIEW_MODE_MINI`),
              value: VIEW_MODES.MINI,
              icon: 'equal',
            },
          ]),
      {
        label: t(`${TP}.FN_VIEW_MODE_GRID`),
        value: VIEW_MODES.GRID,
        icon: 'grid_view',
      },
    ];

    if (isMobile) {
      return validOptions.sort((a, b) => {
        if (a.value === selectedViewMode) {
          return -1;
        }

        if (b.value === selectedViewMode) {
          return 1;
        }

        return 0;
      });
    }

    return validOptions;
  }, [isDesktop, selectedViewMode, isMobile, t]);

  const handleViewModeChange = useCallback(
    value => {
      setViewMode(value);
      navigate.updateQueryParams({
        appendParams: {
          [QUERY_PARAMS_RESERVED.VIEW_MODE]: value,
        },
        deleteParams: [QUERY_PARAMS_RESERVED.SORT_BY],
      });
      resetScrollPosition();
    },
    [navigate, resetScrollPosition, setViewMode],
  );

  return (
    <div className={classes.viewSwitcher}>
      {options.map(option => (
        <QuaternaryButton
          key={option.value}
          styles={{
            root: classnames(classes.viewSwitcher_item, {
              [classes.viewSwitcher_item_selected]: selectedViewMode === option.value,
            }),
          }}
          leftIcon={<SpriteIcon icon={option.icon} size={18} />}
          onClick={() => handleViewModeChange(option.value)}
          size="small"
          {...getLinkProps(option.value)}
          preventDefault
          isLink
          trackingData={{
            section: SECTIONS.PRODUCTION_VIEW_SWITCH,
            meta: {
              id: option?.value,
            },
          }}
        >
          {option.label}
        </QuaternaryButton>
      ))}
    </div>
  );
};

export default ProductionViewSwitcher;
